import React, {useState} from 'react';
import styled from 'styled-components';
import {useRecoilValue} from 'recoil';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';
import {userInfoState} from 'utils/Atoms';
import CommonModal from 'components/common/CommonModal';
import DefaultText from 'components/common/DefaultText';
import DefaultButton from 'components/common/DefaultButton';
import DefaultImageView from 'components/common/DefaultImageView';
import LabelInput from 'components/common/LabelInput';
import {revokeHqGrant} from 'function/api';

type Props = {
  grant: any;
  closeModal: () => void;
  onRevoked: () => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
};

const RevokeHqGrantModal = ({
  grant,
  closeModal,
  onRevoked,
  setIsLoading,
  setErrorModal,
}: Props) => {
  const userInfo = useRecoilValue(userInfoState);
  const [reason, setReason] = useState('');

  const handleRevoke = async () => {
    if (!reason || reason.trim().length === 0) {
      alert('회수 사유를 입력하세요');
      return;
    }
    const result = await revokeHqGrant({
      userInfo,
      setErrorModal,
      setIsLoading,
      grantUUID: grant.uuid,
      reason: reason.trim(),
    });
    if (result && (result as any).revoked) {
      onRevoked();
    }
  };

  return (
    <CommonModal closeModal={closeModal}>
      <ModalContainer>
        <ModalHeader>
          <DefaultText
            text={'권한 회수'}
            textStyle={{fontSize: minmax(28), fontWeight: 'bold'}}
          />
          <DefaultImageView
            source="images/common/close.png"
            onClick={closeModal}
            style={{
              width: minmax(28),
              height: minmax(28),
              cursor: 'pointer',
            }}
          />
        </ModalHeader>

        <Info>
          <Row>
            <Label>대상 user</Label>
            <Value>
              {grant.userInfo?.name ?? '-'}
              {grant.userInfo?.phone ? ` (${grant.userInfo.phone})` : ''}
            </Value>
          </Row>
          <Row>
            <Label>UUID</Label>
            <Value style={{fontSize: minmax(11), color: colors.darkGray}}>{grant.userUUID}</Value>
          </Row>
          <Row>
            <Label>역할</Label>
            <Value>
              <RoleBadge $role={grant.grantRole}>{grant.grantRole}</RoleBadge>
            </Value>
          </Row>
          <Row>
            <Label>부여 시각</Label>
            <Value>{new Date(grant.grantedAt).toLocaleString('ko-KR')}</Value>
          </Row>
        </Info>

        {grant.grantRole === 'SEED' && (
          <Warning>
            ⚠️ SEED 회수 시 같은 시공사의 INVITED 도 자동 회수됩니다.
          </Warning>
        )}

        <Section>
          <LabelInput
            type={'text'}
            labelText={'회수 사유 (필수, 200자)'}
            value={reason}
            setValue={setReason}
          />
        </Section>

        <DefaultButton
          text="회수"
          actived={reason.trim().length > 0 ? 1 : 0}
          buttonStyle={{
            width: '100%',
            backgroundColor: reason.trim().length > 0 ? '#d33' : colors.lightGrey,
            marginTop: minmax(20),
          }}
          textStyle={{fontSize: minmax(20), color: colors.white}}
          onClick={handleRevoke}
        />
      </ModalContainer>
    </CommonModal>
  );
};

export default RevokeHqGrantModal;

const ModalContainer = styled.div`
  background: ${colors.white};
  padding: ${minmax(30)};
  border-radius: ${minmax(10)};
  width: ${minmax(560)};
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${colors.lightGrey};
  padding-bottom: ${minmax(15)};
  margin-bottom: ${minmax(20)};
`;
const Info = styled.div`
  background: ${colors.lightGrey};
  padding: ${minmax(15)};
  border-radius: ${minmax(6)};
  margin-bottom: ${minmax(20)};
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${minmax(8)};
`;
const Label = styled.div`
  width: ${minmax(100)};
  font-weight: bold;
  font-size: ${minmax(14)};
`;
const Value = styled.div`
  flex: 1;
  font-size: ${minmax(14)};
  word-break: break-all;
`;
const Section = styled.div`
  margin-bottom: ${minmax(15)};
`;
const Warning = styled.div`
  background: #fff3cd;
  color: #856404;
  padding: ${minmax(12)};
  border-radius: ${minmax(6)};
  margin-bottom: ${minmax(15)};
  font-weight: bold;
`;
const RoleBadge = styled.span<{$role: string}>`
  display: inline-block;
  padding: ${minmax(4)} ${minmax(10)};
  border-radius: ${minmax(4)};
  font-size: ${minmax(13)};
  font-weight: bold;
  background: ${(p) => (p.$role === 'SEED' ? colors.primary : '#888')};
  color: white;
`;
